/**
 * PointsOpsPage — /agency/points-ops
 *
 * Admin dashboard for the Hustle Points program.
 * Surfaces:
 *   · outstanding pts + AUD equivalent
 *   · 30-day earn vs redeem trend
 *   · top redeemers (last 30 days)
 *   · forfeit-candidate count + one-click 24-month sweep
 *
 * Auth: requires `useAuth()` user; endpoints are admin-gated on the
 * backend, so a non-admin visitor just sees empty state + 401 errors.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, TrendingUp, TrendingDown, Users, Flame, ShieldCheck, ArrowRight } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";
const withCreds = (extra = {}) => ({ credentials: "include", ...extra });

export default function PointsOpsPage() {
  const { user, checking } = useAuth();
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const [rSum, rSet] = await Promise.all([
        fetch(`${API}/api/hustlepoints/ops/summary`, withCreds()),
        fetch(`${API}/api/hustlepoints/ops/settings`, withCreds()),
      ]);
      if (rSum.status === 401 || rSum.status === 403) {
        setErr("Admin only. Sign in with an admin account.");
        return;
      }
      setSummary(await rSum.json());
      if (rSet.ok) {
        const s = await rSet.json();
        setSettings(s);
        setSettingsDraft(s);
      }
    } catch (_e) {
      setErr("Could not load summary.");
    }
  };

  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  const saveSettings = async () => {
    if (!settingsDraft) return;
    setSavingSettings(true);
    try {
      const r = await fetch(`${API}/api/hustlepoints/ops/settings`,
        withCreds({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(settingsDraft),
        }));
      const j = await r.json();
      if (r.ok) {
        setSettings(j);
        setSettingsDraft(j);
        toast.success("Settings saved.");
      } else {
        toast.error(j.detail || "Save failed.");
      }
    } finally {
      setSavingSettings(false);
    }
  };

  const runForfeit = async () => {
    if (!confirm) { setConfirm(true); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/hustlepoints/ops/forfeit`,
        withCreds({ method: "POST" }));
      const j = await r.json();
      if (r.ok) {
        toast.success(`Forfeited ${j.pts_forfeited.toLocaleString("en-AU")} pts from ${j.members_forfeited} inactive accounts = $${j.aud_breakage.toFixed(2)} breakage revenue`);
        setConfirm(false);
        load();
      } else {
        toast.error(j.detail || "Forfeit failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-stone-50">Loading…</div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-stone-50">
        <PlatformHeader brand={{ name: "Points Ops", to: "/agency/points-ops" }} showBookCta={false} showCart={false} />
        <div className="max-w-md mx-auto mt-24 rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-sm">
          <ShieldCheck size={38} className="text-amber-500 mx-auto mb-4"/>
          <h1 className="text-2xl font-black mb-2">Points Ops Console</h1>
          <p className="text-sm text-stone-600 mb-6">Admin sign-in required.</p>
          <Link to="/login?next=/agency/points-ops"
                data-testid="points-ops-signin"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black">
            Sign in <ArrowRight size={14}/>
          </Link>
        </div>
        <SuiteFooter/>
      </div>
    );
  }

  return (
    <div data-testid="points-ops-page" className="min-h-screen bg-stone-50">
      <PlatformHeader brand={{ name: "Points Ops", to: "/agency/points-ops" }} showBookCta={false} showCart={false} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl md:text-4xl font-black text-stone-900">
            <Coins className="inline text-amber-500 mb-1 mr-2" size={30}/>
            Hustle Points Ops
          </h1>
          <button onClick={load}
                  data-testid="points-ops-refresh"
                  className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-stone-800">
            Refresh
          </button>
        </div>

        {err && <div data-testid="points-ops-err" className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">{err}</div>}

        {summary && (
          <>
            {/* Big stats grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="points-ops-stats">
              <StatCard icon={Coins} label="Outstanding pts"
                        value={summary.outstanding_pts.toLocaleString("en-AU")}
                        sub={`~$${summary.outstanding_aud.toFixed(2)} liability`}
                        tone="amber" testid="stat-outstanding"/>
              <StatCard icon={Users} label="Members with balance"
                        value={summary.members_with_balance.toLocaleString("en-AU")}
                        sub="unique accounts" tone="slate" testid="stat-members"/>
              <StatCard icon={TrendingUp} label="Earned (30d)"
                        value={`+${summary.earned_30d.toLocaleString("en-AU")}`}
                        sub="pts issued to members" tone="emerald" testid="stat-earned"/>
              <StatCard icon={TrendingDown} label="Redeemed (30d)"
                        value={`-${summary.redeemed_30d.toLocaleString("en-AU")}`}
                        sub="pts spent at vendors" tone="rose" testid="stat-redeemed"/>
            </section>

            {/* Forfeit block */}
            <section className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm" data-testid="points-ops-forfeit">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Flame className="text-orange-500" size={22}/>
                    <h2 className="text-2xl font-black">Breakage sweep</h2>
                  </div>
                  <p className="text-sm text-stone-600 max-w-lg">
                    <strong>{summary.forfeit_candidates}</strong> accounts have been inactive
                    for 24+ months. Running the sweep forfeits their balances and books the AUD
                    equivalent as breakage revenue (append-only ledger entry
                    `reason="forfeit-inactive"`).
                  </p>
                </div>
                <button
                  onClick={runForfeit}
                  disabled={busy || summary.forfeit_candidates === 0}
                  data-testid="points-ops-forfeit-btn"
                  className={`px-5 py-3 rounded-full font-black text-sm ${
                    confirm
                      ? "bg-rose-600 text-white hover:bg-rose-500"
                      : "bg-stone-900 text-white hover:bg-stone-800"
                  } disabled:opacity-40`}
                >
                  {busy
                    ? "Sweeping…"
                    : confirm
                      ? `Confirm sweep · ${summary.forfeit_candidates} accounts`
                      : "Run sweep…"}
                </button>
              </div>
            </section>

            {/* Settings block */}
            {settings && settingsDraft && (
              <section className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm" data-testid="points-ops-settings">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="text-amber-500" size={22}/>
                  <h2 className="text-2xl font-black">Bonus points settings</h2>
                </div>
                <p className="text-sm text-stone-600 mb-6 max-w-2xl">
                  These control the referral-signup-and-topup bonus awarded to the referrer,
                  and the bonus awarded when a member submits a review. Set to 0 to
                  disable a bonus without removing the earn hooks.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                      Referral bonus (pts, awarded to referrer when referee tops up)
                    </label>
                    <input type="number" min="0" max="1000"
                           value={settingsDraft.referral_topup_pts}
                           onChange={(e) => setSettingsDraft({
                             ...settingsDraft,
                             referral_topup_pts: Number(e.target.value),
                           })}
                           data-testid="points-ops-setting-referral"
                           className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 focus:border-amber-400 focus:outline-none"/>
                    <p className="text-[11px] text-stone-500 mt-1">Current: {settings.referral_topup_pts} pts</p>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                      Verified review bonus (pts, awarded to reviewer)
                    </label>
                    <input type="number" min="0" max="1000"
                           value={settingsDraft.review_pts}
                           onChange={(e) => setSettingsDraft({
                             ...settingsDraft,
                             review_pts: Number(e.target.value),
                           })}
                           data-testid="points-ops-setting-review"
                           className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 focus:border-amber-400 focus:outline-none"/>
                    <p className="text-[11px] text-stone-500 mt-1">Current: {settings.review_pts} pts</p>
                  </div>
                </div>
                <button
                  onClick={saveSettings}
                  disabled={savingSettings}
                  data-testid="points-ops-save-settings"
                  className="px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black text-sm disabled:opacity-40">
                  {savingSettings ? "Saving…" : "Save settings"}
                </button>
              </section>
            )}

            {/* Top redeemers */}
            <section className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
              <h2 className="text-xl font-black mb-4">Top redeemers · last 30 days</h2>
              {summary.top_redeemers_30d.length === 0 ? (
                <div data-testid="points-ops-redeemers-empty"
                     className="text-sm text-stone-500 text-center py-6">
                  No vendor redemptions in the last 30 days yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="points-ops-redeemers">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b">
                      <th className="py-2">#</th>
                      <th className="py-2">Member</th>
                      <th className="py-2 text-right">Pts redeemed</th>
                      <th className="py-2 text-right">~AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.top_redeemers_30d.map((r_, i) => (
                      <tr key={r_.user_email} data-testid={`points-ops-redeemer-${i + 1}`}
                          className="border-b border-stone-100">
                        <td className="py-2 font-black text-amber-500">{i + 1}</td>
                        <td className="py-2">{r_.user_email}</td>
                        <td className="py-2 text-right font-bold text-rose-600">
                          -{r_.pts_redeemed.toLocaleString("en-AU")}
                        </td>
                        <td className="py-2 text-right text-stone-500">
                          ${(r_.pts_redeemed / 10).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
      <SuiteFooter/>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone, testid }) {
  const toneCls = {
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    slate:   "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
  }[tone] || "bg-stone-50 border-stone-200 text-stone-700";
  return (
    <div data-testid={testid}
         className={`rounded-2xl border p-5 ${toneCls}`}>
      <Icon size={20} className="mb-2"/>
      <div className="text-[11px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-3xl font-black mt-1 text-stone-900">{value}</div>
      <div className="text-xs opacity-70 mt-1">{sub}</div>
    </div>
  );
}
